import { useCallback, useEffect, useRef, useState } from "react";
import { syncCheckoutSessionFromStripeWithRetry } from "../lib/stripe.js";
import {
  clearStripeCheckoutSessionId,
  peekStripeCheckoutSessionId,
} from "../lib/checkoutSessionRecovery.js";
import {
  billingErrorCopy,
  isStaleCheckoutSessionError,
  toUserFacingBillingError,
} from "../lib/userFacingErrors.js";

/**
 * Shared logic for recovering a subscription after a Stripe checkout when the
 * webhook hasn't yet written the billing row (common in dev). Auto-retries once
 * on mount and exposes a manual retry.
 *
 * @param {object} params
 * @param {boolean} params.subLoading  Subscription still loading.
 * @param {boolean} params.alreadyResolved  Subscription already present/paid.
 * @param {() => void} [params.onRecovered]  Called after a successful sync.
 */
export function useCheckoutRecovery({
  subLoading,
  alreadyResolved,
  onRecovered,
  syncFn = syncCheckoutSessionFromStripeWithRetry,
}) {
  const [recoverBusy, setRecoverBusy] = useState(false);
  const [recoverErr, setRecoverErr] = useState(null);
  const autoTried = useRef(false);

  const pendingSessionId =
    !subLoading && !alreadyResolved ? peekStripeCheckoutSessionId() : null;

  const recover = useCallback(
    async (sessionId) => {
      const id = String(sessionId ?? "").trim();
      if (!id) return;
      setRecoverBusy(true);
      setRecoverErr(null);
      try {
        await syncFn(id, { retries: 4, baseDelayMs: 900 });
        clearStripeCheckoutSessionId();
        onRecovered?.();
      } catch (e) {
        const raw = e?.message ?? String(e);
        if (isStaleCheckoutSessionError(raw)) {
          clearStripeCheckoutSessionId();
        }
        setRecoverErr(
          toUserFacingBillingError(raw, {
            fallback: billingErrorCopy.couldNotLinkSubscription,
          }),
        );
      } finally {
        setRecoverBusy(false);
      }
    },
    [onRecovered, syncFn],
  );

  useEffect(() => {
    if (subLoading || alreadyResolved || recoverBusy) return;
    const sid = peekStripeCheckoutSessionId();
    if (!sid || autoTried.current) return;
    autoTried.current = true;
    recover(sid);
  }, [subLoading, alreadyResolved, recoverBusy, recover]);

  const retry = useCallback(() => {
    recover(peekStripeCheckoutSessionId());
  }, [recover]);

  return { pendingSessionId, recoverBusy, recoverErr, recover, retry };
}
